import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { HRCampaignService } from 'src/services/hr-campaign.service'
import { HrPageWrapper } from '../_components/hr-page-wrapper.component'
import {
  CurecallButton,
  CurecallList,
  CurecallListHeaders,
  ECurecallButtonAppearance,
  ECurecallSize,
} from '@honestica/curecall-ui'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { ECampaignStatus, ECampaignType, THRCampaign } from 'src/types/hr'
import { ReactNode } from 'react'
import dayjs from 'dayjs'
import { twMerge } from 'src/utils/tw-merge.util'

type TCampaignHeaderName = 'Type' | 'SentAt' | 'Status' | 'RecipientCount' | 'Actions'
type TTableRow = { [key in TCampaignHeaderName]: ReactNode }

const CampaignTypeBadge = ({ type }: Readonly<{ type: ECampaignType }>) => {
  const { t } = useTranslation()
  const labels: Record<ECampaignType, string> = {
    [ECampaignType.ALERT]: t('pages.hr.campaigns.type.ALERT'),
    [ECampaignType.PLAN_BLANC]: t('pages.hr.campaigns.type.PLAN_BLANC'),
  }
  return (
    <span
      className={twMerge(
        'c-px-2 c-py-0.5 c-rounded-full c-text-xs c-font-medium',
        type === ECampaignType.PLAN_BLANC ? 'c-bg-blue-200 c-text-blue-600' : 'c-bg-red-100 c-text-red-700',
      )}
    >
      {labels[type]}
    </span>
  )
}

const CampaignStatusBadge = ({ status }: Readonly<{ status: ECampaignStatus }>) => {
  const { t } = useTranslation()
  const labels: Record<ECampaignStatus, string> = {
    [ECampaignStatus.SENDING]: t('pages.hr.campaigns.status.SENDING'),
    [ECampaignStatus.SENT]: t('pages.hr.campaigns.status.SENT'),
  }
  return (
    <span
      className={twMerge(
        'c-px-2 c-py-0.5 c-rounded-full c-text-xs c-font-medium',
        status === ECampaignStatus.SENT
          ? 'c-bg-green-100 c-text-green-700'
          : 'c-bg-yellow-100 c-text-yellow-700',
      )}
    >
      {labels[status]}
    </span>
  )
}

export default function HrCampaignsPage() {
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const navigate = useLocalizedNavigate()

  const {
    isLoading,
    error,
    data: campaigns,
  } = useQuery({
    queryKey: ['hr-campaigns', project.id],
    queryFn: () => HRCampaignService.getCampaigns(project.id),
  })

  if (error) {
    return <ErrorQueryHandler error={error} />
  }

  const rows: TTableRow[] =
    campaigns?.map((campaign: THRCampaign) => ({
      Type: <CampaignTypeBadge type={campaign.type} />,
      SentAt: <span>{dayjs(campaign.sentAt).format('DD/MM/YYYY HH:mm')}</span>,
      Status: <CampaignStatusBadge status={campaign.status} />,
      RecipientCount: <span>{campaign.recipientCount}</span>,
      Actions: (
        <CurecallButton
          appearance={ECurecallButtonAppearance['PRIMARY-OUTLINED']}
          size={ECurecallSize.SMALL}
          onClick={() =>
            navigate(
              `/${project.id}/hr/campaigns/${campaign.type === ECampaignType.ALERT ? 'alert' : 'plan-blanc'}/${campaign.id}`,
            )
          }
        >
          {t('common.actions.view')}
        </CurecallButton>
      ),
    })) ?? []

  const headers: CurecallListHeaders<TTableRow> = [
    { label: t('pages.hr.campaigns.table.type'), key: 'Type' },
    { label: t('pages.hr.campaigns.table.sent-at'), key: 'SentAt' },
    { label: t('pages.hr.campaigns.table.status'), key: 'Status' },
    { label: t('pages.hr.campaigns.table.recipient-count'), key: 'RecipientCount' },
    { label: '', key: 'Actions' },
  ]

  return (
    <HrPageWrapper
      title={t('pages.hr.campaigns.title')}
      actions={
        <CurecallButton
          appearance={ECurecallButtonAppearance.PRIMARY}
          size={ECurecallSize.SMALL}
          onClick={() => navigate(`/${project.id}/hr/campaigns/new`)}
        >
          {t('pages.hr.campaigns.new-button')}
        </CurecallButton>
      }
    >
      {isLoading && <div className="loading" />}
      {campaigns && campaigns.length === 0 && (
        <p className="c-text-grey-500 c-mt-4">{t('pages.hr.campaigns.empty')}</p>
      )}
      {campaigns && campaigns.length > 0 && <CurecallList rowsData={rows} headersData={headers} />}
    </HrPageWrapper>
  )
}
